import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Check,
  ChevronDown,
  Folder,
  GripVertical,
  Paperclip,
  Pencil,
  Plus,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import {
  downloadBlobAsFile,
  fetchDownloadSignedUrl,
} from 'components/FileManagement/hooks/useNotebookQueries';
import type { NotebookFile } from 'components/FileManagement/hooks/useGetAllFiles';
import {
  checklistAttachmentKey,
  checklistItemExtraCount,
  checklistItemStatus,
  hwcgChecklistStatusLabel,
  HWCG_CHECKLIST_STATUS_OPTIONS,
  moveChecklistItem,
  removeChecklistAttachment,
  setChecklistAttachments,
  setChecklistComments,
  setChecklistNote,
  setChecklistResponsibleIndividual,
  setChecklistStatus,
  DEFAULT_CHECKLIST_STATUS,
  type ActionChecklistItem,
  type ChecklistItemStatus,
  type ChecklistFileAttachment,
} from './actionChecklistModel';
import { responsibleIndividualDisplayOptions } from './hooks/responsibleIndividuals';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ChecklistFilePicker from './actionTicket/ChecklistFilePicker';
import { ItemCommentsThread } from './actionTicket/ItemCommentsThread';
import type { TaskComment } from './actionTicket/actionTicketModel';
import { useWorkspaceFileMap } from './actionTicket/WorkspaceFilePicker';
import {
  isPreviewableDocument,
  previewMimeType,
} from './actionTicket/documentPreview';
import { TicketSection } from './actionTicket/TicketSection';
import { InlineSelectCell } from './actionsTable/InlineSelectCell';
import { actionStatusBadgeVariant } from './actionsTable/actionEditModel';
import { visibleActionStatusOptions } from './ICSConstants';
import { useHwcgFlag } from 'utils/featureFlags';
import { SubTaskCompletionRing } from './SubTaskCompletionRing';

const ChecklistAttachmentChip: React.FC<{
  file: ChecklistFileAttachment;
  files: Map<string, NotebookFile>;
  onRemove?: () => void;
}> = ({ file, files, onRemove }) => {
  const preview = useOptionalIcsFormPdfPreview();
  const isFolder = file.kind === 'folder';
  const resolved =
    !isFolder && file.fileId ? files.get(file.fileId) : undefined;
  const label =
    file.label ||
    resolved?.file_name ||
    (isFolder ? 'Attached folder' : 'Attached file');
  const canPreview = Boolean(
    !isFolder &&
      preview &&
      resolved?.fileUrl &&
      isPreviewableDocument(label, resolved.mimetype)
  );

  const openPreview = async () => {
    if (!preview || !resolved?.fileUrl) return;
    try {
      const signed = await fetchDownloadSignedUrl(resolved.fileUrl);
      preview.previewFromSignedUrl({
        fileUrl: signed,
        fileName: label,
        mimeType: previewMimeType(label, resolved.mimetype),
        onDownload: () => downloadBlobAsFile(resolved.fileUrl, label),
      });
    } catch {
      toast.error('Unable to open preview.');
    }
  };

  return (
    <Badge variant="secondary" className="tw:gap-1 tw:pr-1">
      {isFolder ? (
        <Folder className="tw:h-3 tw:w-3" />
      ) : (
        <Paperclip className="tw:h-3 tw:w-3" />
      )}
      {canPreview ? (
        <button
          type="button"
          className="tw:max-w-48 tw:truncate tw:border-0 tw:bg-transparent tw:p-0 tw:text-inherit tw:underline tw:underline-offset-2"
          aria-label={`Preview ${label}`}
          onClick={() => {
            void openPreview();
          }}
        >
          {label}
        </button>
      ) : (
        <span className="tw:max-w-48 tw:truncate">{label}</span>
      )}
      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`Remove ${label}`}
          onClick={onRemove}
        >
          <X />
        </Button>
      ) : null}
    </Badge>
  );
};

const NONE = '__none__';

const ChecklistResponsibleSelect: React.FC<{
  index: number;
  item: ActionChecklistItem;
  options: { label: string; value: string }[];
  onChange: (userId: string | null) => void;
}> = ({ index, item, options, onChange }) => {
  const displayOptions = responsibleIndividualDisplayOptions(
    options,
    item.responsible_individual_id ? [item.responsible_individual_id] : [],
    options
  );
  const current = item.responsible_individual_id ?? NONE;
  const items = [
    { value: NONE, label: 'None' },
    ...displayOptions.map((option) => ({
      value: option.value,
      label: option.label,
    })),
  ];
  const hasAssignees = options.length > 0;

  return (
    <Select
      value={current}
      items={items}
      disabled={!hasAssignees}
      onValueChange={(next) => {
        if (next == null || next === NONE) {
          onChange(null);
          return;
        }
        onChange(String(next));
      }}
    >
      <SelectTrigger
        className="tw:w-[11rem] tw:shrink-0"
        aria-label={`Responsible person for sub-task ${index + 1}`}
      >
        <SelectValue
          placeholder={
            hasAssignees ? 'Responsible person' : 'Assign a position first'
          }
        />
      </SelectTrigger>
      <SelectContent className="dark">
        <SelectItem value={NONE}>None</SelectItem>
        {displayOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

/**
 * Grip-only drag, matching objectives: the row holds inputs and buttons, so the
 * listeners stay on the grip.
 */
const SortableSubTask: React.FC<{
  id: string;
  label: string;
  children: (grip: React.ReactNode) => React.ReactNode;
}> = ({ id, label, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const grip = (
    <button
      type="button"
      className="tw:cursor-grab tw:text-muted-foreground tw:active:cursor-grabbing"
      aria-label={`Reorder ${label}`}
      title="Drag to reorder (or use arrow keys)"
      {...attributes}
      {...listeners}
    >
      <GripVertical className="tw:size-4" />
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`tw:rounded-md tw:border tw:border-border tw:bg-muted/20 tw:p-3${isDragging ? 'tw:opacity-40' : ''}`}
    >
      {children(grip)}
    </div>
  );
};

interface ICSChecklistProps {
  checklist?: ActionChecklistItem[];
  setChecklist: (checklist: ActionChecklistItem[]) => void;
  editable?: boolean;
  existingData?: boolean;
  /** Scopes the file picker; absent for an org action. */
  incidentId?: string | null;
  /** Read-only surfaces (the table's expanded row) pass this. */
  readOnly?: boolean;
  /** Ticket layout wraps the list so it matches Requirements / Hazards. */
  asSection?: boolean;
  /** People who can be the responsible person for a sub-task. */
  responsibleIndividualOptions?: { label: string; value: string }[];
  /** Author stamped on a step comment. Without it the thread is read-only. */
  currentUserGuid?: string;
  currentUserName?: string;
}

export default function ICSChecklist({
  checklist = [],
  setChecklist,
  editable = false,
  existingData = false,
  incidentId,
  readOnly = false,
  asSection = false,
  responsibleIndividualOptions = [],
  currentUserGuid = '',
  currentUserName,
}: ICSChecklistProps) {
  const files = useWorkspaceFileMap(incidentId);
  const [expanded, setExpanded] = useState<number | null>(null);
  /**
   * HWCG: one item drafts locally until Save so tip versions aren't per
   * keystroke.
   */
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [itemDraft, setItemDraft] = useState<ActionChecklistItem | null>(null);
  /** True while drafting a row that is not yet in `checklist`. */
  const [insertingNew, setInsertingNew] = useState(false);
  const hwcg = useHwcgFlag();
  const itemEditMode = hwcg && editable && !readOnly;

  const handleLabelChange = (index: number, newLabel: string) => {
    if (itemEditMode && editingIndex === index && itemDraft) {
      setItemDraft({ ...itemDraft, label: newLabel });
      return;
    }
    const updatedChecklist = checklist.map((item, idx) =>
      idx === index ? { ...item, label: newLabel } : item
    );
    setChecklist(updatedChecklist);
  };

  const handleStatusChange = (index: number, status: ChecklistItemStatus) => {
    if (itemEditMode && editingIndex === index && itemDraft) {
      setItemDraft({
        ...itemDraft,
        status,
        checked: status === 'Closed',
      });
      return;
    }
    setChecklist(setChecklistStatus(checklist, index, status));
  };

  const beginItemEdit = (index: number) => {
    setInsertingNew(false);
    setEditingIndex(index);
    setItemDraft({ ...checklist[index] });
    setExpanded(index);
  };

  const cancelItemEdit = () => {
    setEditingIndex(null);
    setItemDraft(null);
    setInsertingNew(false);
  };

  const saveItemEdit = () => {
    if (editingIndex == null || !itemDraft) return;
    const next = insertingNew
      ? [itemDraft, ...checklist]
      : checklist.map((item, idx) => (idx === editingIndex ? itemDraft : item));
    setChecklist(next);
    setEditingIndex(null);
    setItemDraft(null);
    setInsertingNew(false);
  };

  const addItem = () => {
    const blank: ActionChecklistItem = {
      label: '',
      status: DEFAULT_CHECKLIST_STATUS,
      checked: false,
      comments: [],
    };
    if (itemEditMode) {
      setInsertingNew(true);
      setEditingIndex(0);
      setItemDraft(blank);
      setExpanded(0);
      return;
    }
    setChecklist([blank, ...checklist]);
    if (expanded !== null) setExpanded(expanded + 1);
  };

  const removeItem = (index: number) => {
    if (itemEditMode && insertingNew && editingIndex === index) {
      cancelItemEdit();
      return;
    }
    if (itemEditMode && editingIndex === index) {
      cancelItemEdit();
    } else if (itemEditMode && editingIndex != null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
    setChecklist(checklist.filter((_, idx) => idx !== index));
    if (expanded === index) setExpanded(null);
  };

  const canReorder = editable && !readOnly;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (itemEditMode && editingIndex != null) return;
    const next = moveChecklistItem(
      checklist,
      Number(active.id),
      Number(over.id)
    );
    if (next === checklist) return;
    setExpanded(null);
    setChecklist(next);
  };

  /**
   * Notes and workspace files are on every step. The table expanded row
   * (`readOnly`) only displays what was saved.
   */
  const extrasEditable = !readOnly;
  const statusOptions = hwcg
    ? [...HWCG_CHECKLIST_STATUS_OPTIONS]
    : visibleActionStatusOptions(hwcg);
  const statusDisabled = readOnly || !existingData;

  const renderReadOnlyExtras = (item: ActionChecklistItem) => {
    const attachments = item.attachments ?? [];
    const note = (item.note ?? '').trim();
    if (!note && attachments.length === 0) return null;

    return (
      <div className="tw:mt-2 tw:flex tw:flex-col tw:gap-1 tw:pl-8">
        {note ? (
          <p className="tw:m-0 tw:text-sm tw:whitespace-pre-wrap tw:text-muted-foreground">
            {note}
          </p>
        ) : null}
        {attachments.length > 0 ? (
          <ul className="tw:m-0 tw:flex tw:list-none tw:flex-wrap tw:gap-2 tw:p-0">
            {attachments.map((file) => (
              <li key={checklistAttachmentKey(file)}>
                <ChecklistAttachmentChip file={file} files={files} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };

  const renderExtras = (
    item: ActionChecklistItem,
    index: number,
    isEditingItem: boolean
  ) => {
    const attachments = item.attachments ?? [];
    const count = checklistItemExtraCount(item);
    const isOpen = isEditingItem ? true : expanded === index;

    return (
      <div className="tw:mt-2 tw:flex tw:flex-col tw:gap-2 tw:pl-8">
        {isOpen ? (
          <>
            <Textarea
              value={item.note ?? ''}
              placeholder="What happened on this step?"
              aria-label={
                item.label ? `Note for ${item.label}` : 'Note for this step'
              }
              rows={3}
              disabled={itemEditMode && !isEditingItem}
              onChange={(event) => {
                if (itemEditMode && editingIndex === index && itemDraft) {
                  setItemDraft({ ...itemDraft, note: event.target.value });
                  return;
                }
                setChecklist(
                  setChecklistNote(checklist, index, event.target.value)
                );
              }}
            />
            {attachments.length > 0 ? (
              <ul className="tw:m-0 tw:flex tw:list-none tw:flex-wrap tw:gap-2 tw:p-0">
                {attachments.map((file) => (
                  <li key={checklistAttachmentKey(file)}>
                    <ChecklistAttachmentChip
                      file={file}
                      files={files}
                      onRemove={() => {
                        const key = checklistAttachmentKey(file);
                        // While drafting this row, keep attaches local until Save.
                        if (itemEditMode && isEditingItem && itemDraft) {
                          setItemDraft(
                            removeChecklistAttachment([itemDraft], 0, key)[0]
                          );
                          return;
                        }
                        // Outside edit: persist tip immediately (new version).
                        setChecklist(
                          removeChecklistAttachment(checklist, index, key)
                        );
                      }}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
            <div>
              <ChecklistFilePicker
                incidentId={incidentId}
                selected={attachments}
                allowFolders={hwcg}
                onChange={(next) => {
                  // While drafting this row, keep attaches local until Save.
                  if (itemEditMode && isEditingItem && itemDraft) {
                    setItemDraft({ ...itemDraft, attachments: next });
                    return;
                  }
                  // Outside edit: persist tip immediately (new version).
                  setChecklist(setChecklistAttachments(checklist, index, next));
                }}
              />
            </div>
          </>
        ) : null}
        {isEditingItem ? null : (
          <div className="tw:flex tw:items-center tw:gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={isOpen}
              onClick={() => setExpanded(isOpen ? null : index)}
            >
              {isOpen ? <ChevronDown /> : <StickyNote />}
              {isOpen ? 'Hide notes' : 'Notes'}
            </Button>
            {count > 0 && !isOpen ? (
              <Badge variant="secondary">{count}</Badge>
            ) : null}
          </div>
        )}
        {/* HWCG: attach/remove without entering item edit (collapsed notes). */}
        {hwcg && extrasEditable && !isOpen ? (
          <div className="tw:flex tw:flex-col tw:gap-2">
            {attachments.length > 0 ? (
              <ul className="tw:m-0 tw:flex tw:list-none tw:flex-wrap tw:gap-2 tw:p-0">
                {attachments.map((file) => (
                  <li key={checklistAttachmentKey(file)}>
                    <ChecklistAttachmentChip
                      file={file}
                      files={files}
                      onRemove={() => {
                        setChecklist(
                          removeChecklistAttachment(
                            checklist,
                            index,
                            checklistAttachmentKey(file)
                          )
                        );
                      }}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
            <ChecklistFilePicker
              incidentId={incidentId}
              selected={attachments}
              allowFolders
              onChange={(next) => {
                setChecklist(setChecklistAttachments(checklist, index, next));
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  /**
   * HWCG step comments persist through `setChecklist` like notes do. While the
   * row is being edited the draft is kept in step, so Save cannot overwrite a
   * comment posted mid-edit with the stale copy the draft started from.
   */
  const postStepComments = (index: number, next: TaskComment[]) => {
    setChecklist(setChecklistComments(checklist, index, next));
    if (itemEditMode && editingIndex === index && itemDraft && !insertingNew) {
      setItemDraft({ ...itemDraft, comments: next });
    }
  };

  const renderComments = (
    item: ActionChecklistItem,
    index: number,
    rank: number
  ) => {
    const threadReadOnly = readOnly || !editable || !currentUserGuid;
    if (threadReadOnly && (item.comments ?? []).length === 0) return null;
    return (
      <div className="tw:mt-2 tw:pl-8">
        <ItemCommentsThread
          value={item.comments ?? []}
          onPost={(next) => postStepComments(index, next)}
          readOnly={threadReadOnly}
          currentUserGuid={currentUserGuid}
          currentUserName={currentUserName}
          label={`sub-task ${rank}`}
          collapsible
        />
      </div>
    );
  };

  const addButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={addItem}
      disabled={itemEditMode && editingIndex != null}
    >
      <Plus />
      Add sub-task
    </Button>
  );

  type RowSource = {
    item: ActionChecklistItem;
    /** Index into `checklist`, or 0 while inserting a new draft row. */
    index: number;
    rank: number;
    virtual: boolean;
  };

  const rowSources: RowSource[] = [];
  if (insertingNew && itemDraft) {
    rowSources.push({ item: itemDraft, index: 0, rank: 1, virtual: true });
  }
  checklist.forEach((stored, index) => {
    const item =
      !insertingNew && editingIndex === index && itemDraft ? itemDraft : stored;
    rowSources.push({
      item,
      index,
      rank: insertingNew ? index + 2 : index + 1,
      virtual: false,
    });
  });

  const rows = rowSources.map(({ item, index, rank, virtual }) => {
    const status = checklistItemStatus(item);
    const displayStatus = hwcg ? hwcgChecklistStatusLabel(status) : status;
    const isComplete = status === 'Closed';
    const isEditingItem =
      itemEditMode &&
      editingIndex === index &&
      itemDraft != null &&
      (virtual ? insertingNew : !insertingNew);
    const statusBadge = (
      <Badge variant={actionStatusBadgeVariant(status)}>{displayStatus}</Badge>
    );
    const rankEl = (
      <span className="tw:w-5 tw:shrink-0 tw:text-center tw:text-xs tw:tabular-nums tw:text-muted-foreground">
        {rank}
      </span>
    );
    const body = (grip: React.ReactNode) => (
      <>
        <div className="tw:flex tw:items-center tw:gap-2">
          {grip}
          {rankEl}
          {hwcg ? (
            <Checkbox
              checked={isComplete}
              disabled={
                statusDisabled ||
                !editable ||
                (itemEditMode && insertingNew && virtual) ||
                (itemEditMode && editingIndex != null && !isEditingItem)
              }
              aria-label={`Mark sub-task ${rank} complete`}
              onCheckedChange={(checked) => {
                if (statusDisabled || !editable) return;
                handleStatusChange(
                  index,
                  checked === true ? 'Closed' : 'Planned'
                );
              }}
            />
          ) : null}
          {editable &&
          !readOnly &&
          !(itemEditMode && editingIndex != null && !isEditingItem) ? (
            <InlineSelectCell
              options={statusOptions}
              value={status}
              onChange={(next) => {
                if (!next) return;
                handleStatusChange(index, next as ChecklistItemStatus);
              }}
              disabled={statusDisabled}
              ariaLabel={`Status for sub-task ${rank}`}
              placeholder="Set status"
              searchPlaceholder="Search statuses"
              field="status"
            >
              {statusBadge}
            </InlineSelectCell>
          ) : (
            statusBadge
          )}
          {editable && itemEditMode ? (
            isEditingItem ? (
              <>
                <Input
                  value={item.label}
                  placeholder="Enter sub-task"
                  aria-label={`Sub-task ${rank}`}
                  onChange={(event) =>
                    handleLabelChange(index, event.target.value)
                  }
                  className="tw:flex-1"
                />
                <ChecklistResponsibleSelect
                  index={index}
                  item={item}
                  options={responsibleIndividualOptions}
                  onChange={(userId) => {
                    if (!itemDraft) return;
                    const nextId = (userId ?? '').trim();
                    if (!nextId) {
                      const { responsible_individual_id: _dropped, ...rest } =
                        itemDraft;
                      setItemDraft(rest);
                      return;
                    }
                    setItemDraft({
                      ...itemDraft,
                      responsible_individual_id: nextId,
                    });
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Save sub-task ${rank}`}
                  onClick={saveItemEdit}
                >
                  <Check className="tw:size-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Cancel sub-task ${rank}`}
                  onClick={cancelItemEdit}
                >
                  <X className="tw:size-4" />
                </Button>
                {!virtual ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove sub-task ${rank}`}
                    onClick={() => removeItem(index)}
                  >
                    <Trash2 />
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                <span className="tw:m-0 tw:min-w-0 tw:flex-1 tw:text-sm">
                  {item.label || (
                    <span className="tw:text-muted-foreground">
                      Untitled sub-task
                    </span>
                  )}
                  {item.responsible_individual_id
                    ? ` · ${
                        responsibleIndividualDisplayOptions(
                          responsibleIndividualOptions,
                          [item.responsible_individual_id],
                          responsibleIndividualOptions
                        ).find(
                          (option) =>
                            option.value === item.responsible_individual_id
                        )?.label ?? item.responsible_individual_id
                      }`
                    : ''}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Edit sub-task ${rank}`}
                  onClick={() => beginItemEdit(index)}
                  disabled={editingIndex != null}
                >
                  <Pencil className="tw:size-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove sub-task ${rank}`}
                  onClick={() => removeItem(index)}
                  disabled={editingIndex != null}
                >
                  <Trash2 />
                </Button>
              </>
            )
          ) : editable ? (
            <>
              <Input
                value={item.label}
                placeholder="Enter sub-task"
                aria-label={`Sub-task ${rank}`}
                onChange={(event) =>
                  handleLabelChange(index, event.target.value)
                }
                className="tw:flex-1"
              />
              <ChecklistResponsibleSelect
                index={index}
                item={item}
                options={responsibleIndividualOptions}
                onChange={(userId) =>
                  setChecklist(
                    setChecklistResponsibleIndividual(checklist, index, userId)
                  )
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove sub-task ${rank}`}
                onClick={() => removeItem(index)}
              >
                <Trash2 />
              </Button>
            </>
          ) : (
            <span className="tw:m-0 tw:min-w-0 tw:flex-1 tw:text-sm">
              {item.label}
              {item.responsible_individual_id
                ? ` · ${
                    responsibleIndividualDisplayOptions(
                      responsibleIndividualOptions,
                      [item.responsible_individual_id],
                      responsibleIndividualOptions
                    ).find(
                      (option) =>
                        option.value === item.responsible_individual_id
                    )?.label ?? item.responsible_individual_id
                  }`
                : ''}
            </span>
          )}
        </div>
        {extrasEditable
          ? renderExtras(item, index, isEditingItem)
          : renderReadOnlyExtras(item)}
        {hwcg && !virtual ? renderComments(item, index, rank) : null}
      </>
    );

    const rowKey = virtual ? 'new-subtask' : String(index);

    if (!canReorder || virtual || isEditingItem) {
      return (
        <div
          key={rowKey}
          className="tw:rounded-md tw:border tw:border-border tw:bg-muted/20 tw:p-3"
        >
          {body(null)}
        </div>
      );
    }

    return (
      <SortableSubTask
        key={rowKey}
        id={String(index)}
        label={`sub-task ${rank}`}
      >
        {body}
      </SortableSubTask>
    );
  });

  const completion = <SubTaskCompletionRing checklist={checklist} />;

  const listBody = (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {rows}
      {editable && !asSection ? addButton : null}
    </div>
  );

  const list = canReorder ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={checklist.map((_, index) => String(index))}
        strategy={verticalListSortingStrategy}
      >
        {listBody}
      </SortableContext>
    </DndContext>
  ) : (
    listBody
  );

  if (asSection) {
    return (
      <TicketSection
        title={hwcg ? 'Prerequisite Checklist' : 'Sub-Tasks'}
        count={checklist.length || undefined}
        actions={
          <div className="tw:flex tw:items-center tw:gap-2">
            {completion}
            {editable ? addButton : null}
          </div>
        }
      >
        {list}
      </TicketSection>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {completion}
      {list}
    </div>
  );
}
